import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Icon } from "../../components/Icon";
import { Badge } from "../../components/ui";
import { haptic } from "../../telegram/haptics";
import { micro, pressSpring } from "../../motion";
import { useT } from "../../i18n/strings";
import { fieldStyles as f } from "../../components/fields";
import s from "../pieces/pieces.module.css";

const DEBOUNCE_MS = 250;

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  filterCount: number;
  onOpenFilters: () => void;
}

/**
 * Debounced so a five-letter query is one request, not five — the search hits
 * a tsvector plus a trigram fallback and is not free.
 */
export function SearchBar({ value, onChange, filterCount, onOpenFilters }: SearchBarProps) {
  const t = useT();
  const [draft, setDraft] = useState(value);
  const committed = useRef(value);

  // Follow external resets (Clear all) without fighting the user's typing.
  useEffect(() => {
    if (value !== committed.current) {
      committed.current = value;
      setDraft(value);
    }
  }, [value]);

  useEffect(() => {
    if (draft === committed.current) return;
    const timer = window.setTimeout(() => {
      committed.current = draft;
      onChange(draft);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, onChange]);

  return (
    <div className={s.searchWrap}>
      <div className={f.searchBox}>
        <Icon name="search" size={20} />
        <input
          type="search"
          dir="auto"
          className={f.searchInput}
          value={draft}
          placeholder={t.home.searchPlaceholder}
          aria-label={t.actions.search}
          onChange={(event) => setDraft(event.target.value)}
        />
        <AnimatePresence>
          {draft && (
            <motion.button
              type="button"
              className={f.searchClear}
              aria-label={t.actions.clearAll}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={micro}
              onClick={() => {
                haptic.tap();
                setDraft("");
              }}
            >
              <Icon name="close" size={16} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <motion.button
        type="button"
        className={[s.filterButton, filterCount > 0 && s.filterButtonActive]
          .filter(Boolean)
          .join(" ")}
        aria-label={t.actions.filter}
        whileTap={{ scale: 0.94 }}
        transition={pressSpring}
        onClick={onOpenFilters}
      >
        <Icon name="filter" size={20} />
        {filterCount > 0 && (
          <motion.span
            className={s.filterBadge}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={micro}
          >
            <Badge value={filterCount} />
          </motion.span>
        )}
      </motion.button>
    </div>
  );
}
